import { useState } from 'react';
import { Modal } from '../Modal';
import { CheckCircle2, AlertCircle, User, Calendar, Clock, FileAudio } from 'lucide-react';
import { toast } from 'sonner';
import { saveEntity } from '../../services/api';

interface ApproveAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  audit?: {
    id: string;
    approvalId: string;
    operatorName: string;
    date: string;
    score: number;
    duration: number;
  };
}

export function ApproveAuditModal({ isOpen, onClose, onSuccess, audit }: ApproveAuditModalProps) {
  const [notes, setNotes] = useState('');
  const [isApproving, setIsApproving] = useState(false);

  const handleApprove = async () => {
    setIsApproving(true);

    try {
      if (audit?.approvalId) {
        await saveEntity('pending-approvals', {
          id: audit.approvalId,
          action: 'approve',
          reason: notes || undefined,
        } as any);
      }
      toast.success('Auditoria aprovada com sucesso!', {
        description: `Auditoria de ${audit?.operatorName} foi aprovada e notificada.`,
      });
      setNotes('');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('[ApproveAuditModal] erro ao aprovar:', error);
      toast.error('Falha ao aprovar auditoria.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!notes.trim()) {
      toast.error('Adicione um comentário explicando a rejeição');
      return;
    }

    setIsApproving(true);

    try {
      if (audit?.approvalId) {
        await saveEntity('pending-approvals', {
          id: audit.approvalId,
          action: 'reject',
          reason: notes,
        } as any);
      }
      toast.warning('Auditoria rejeitada', {
        description: `Auditoria de ${audit?.operatorName} foi rejeitada e retornada para revisão.`,
      });
      setNotes('');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('[ApproveAuditModal] erro ao reprovar:', error);
      toast.error('Falha ao reprovar auditoria.');
    } finally {
      setIsApproving(false);
    }
  };

  if (!audit) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Aprovar Auditoria"
      size="md"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={isApproving}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: 'var(--foreground)',
              cursor: isApproving ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-medium)',
              opacity: isApproving ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleReject}
            disabled={isApproving}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--destructive)',
              backgroundColor: 'transparent',
              color: 'var(--destructive)',
              cursor: isApproving ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-semibold)',
              opacity: isApproving ? 0.5 : 1,
            }}
          >
            Rejeitar
          </button>
          <button
            onClick={handleApprove}
            disabled={isApproving}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              border: 'none',
              cursor: isApproving ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-semibold)',
              opacity: isApproving ? 0.5 : 1,
            }}
          >
            {isApproving ? 'Aprovando...' : 'Aprovar'}
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Audit Info */}
        <div
          style={{
            backgroundColor: 'var(--muted)',
            padding: '16px',
            borderRadius: 'var(--radius)',
          }}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User size={18} style={{ color: 'var(--muted-foreground)' }} />
              <div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Operador</p>
                <p style={{ fontWeight: 'var(--font-weight-semibold)' }}>{audit.operatorName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar size={18} style={{ color: 'var(--muted-foreground)' }} />
              <div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Data</p>
                <p style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  {new Date(audit.date).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock size={18} style={{ color: 'var(--muted-foreground)' }} />
              <div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Duração</p>
                <p style={{ fontWeight: 'var(--font-weight-semibold)' }}>{audit.duration} min</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FileAudio size={18} style={{ color: 'var(--muted-foreground)' }} />
              <div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Pontuação</p>
                <p
                  style={{
                    fontWeight: 'var(--font-weight-semibold)',
                    fontSize: 'var(--text-lg)',
                    color: audit.score === 0 ? 'var(--destructive)' : audit.score >= 70 ? 'var(--primary)' : 'var(--warning)',
                  }}
                >
                  {audit.score}/100
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Score Alert */}
        {audit.score === 0 && (
          <div
            style={{
              backgroundColor: 'var(--destructive)',
              color: 'var(--destructive-foreground)',
              padding: '12px 16px',
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: '4px' }}>
                Falha Grave Detectada
              </p>
              <p style={{ fontSize: 'var(--text-sm)' }}>
                Esta auditoria teve nota zerada devido à regra "Falha Grave Zera". Revise cuidadosamente antes de aprovar.
              </p>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label
            htmlFor="notes"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Comentários (opcional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione observações sobre esta auditoria..."
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--background)',
              color: 'var(--foreground)',
              fontSize: 'var(--text-base)',
              resize: 'vertical',
            }}
          />
        </div>

        {/* Info */}
        <div
          style={{
            backgroundColor: 'var(--muted)',
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <CheckCircle2 size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
            Ao aprovar, o operador será notificado e a auditoria será marcada como concluída.
          </p>
        </div>
      </div>
    </Modal>
  );
}
